import { faCreativeCommonsBy } from "@fortawesome/free-brands-svg-icons/faCreativeCommonsBy";
import { faPresentationScreen } from "@fortawesome/pro-solid-svg-icons/faPresentationScreen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card, CardContent, Grid, IconButton, Skeleton, Typography } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcClickable, MprcContainer, MprcJoinComponents, MprcRequestErrorReport, MprcSpinnerIndicator } from "components";
import { IconAI } from "components/icons";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { MprcSwitchPlayback } from "components/MprcSwitchPlayback";
import { CustomVideoConfirmSeekResult } from "components/types/lightBox";
import { FeedAttachDto, FeedContentLoadStatus, FeedItemDto, IaRequestCallingStatus, PublicationAttachType, TrackingContentResponse, TrainingPostItemDto } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { useSystemUsages } from "hooks/useSystemUsages";
import { Formaters } from "lib/Formaters";
import { useCoreApi } from "providers/CoreApiProvider";
import { useModalUserProfile } from "providers/UserProfileProvider";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { OnProgressProps } from "react-player/base";
import { NIL } from "uuid";
import { CardOwnPropsOptions } from "views/knowledge/components/card";
import { PublicationAiSearchDialog } from "views/knowledge/components/card/actions/PublicationAiSearchDialog";
import { BuildLigthBoxSlides, CardLightBox } from "views/knowledge/components/card/behaviors/common/CardLightbox";
import { InternalContentMedia } from "views/knowledge/components/card/behaviors/common/ContentMedia";
import { InternalAttach } from "views/knowledge/components/card/behaviors/common/DefaultAttach";
import FeedItemHelper from "views/knowledge/components/card/FeedItemHelper";
import { PrepareCardOwnProps } from "views/knowledge/components/card/PrepareCardOwnProps";
import { useEnrollmentViewContext } from "views/lms/student/EnrollmentView";

/////////////////////////////////////////////////////////////////////////////////////

const TrackingSeconds = 5;
const TrackingLowerThresholdSeconds = 5;

interface IProps {
    enrollmentId?: string;
    componentId?: string;
    publicationId?: string;
    onComplete: () => void;
    onPresentationMedia?: (attachType: PublicationAttachType) => void;
    onAttachOpen?: () => void;
    onAttachClose?: () => void;
    readOnly?: boolean;
}

type TrackComponentStatus = {
    publicationViewed: boolean;
    mediaCompleted: boolean;
    attachOpened: boolean;
    timeLeft: number;
};

/////////////////////////////////////////////////////////////////////////////////////

export const PostContent = (props: IProps) => {

    const { enrollmentId, componentId, publicationId, readOnly, onComplete, onAttachClose, onAttachOpen, onPresentationMedia } = props;

    const enrollmentViewContext = useEnrollmentViewContext();
    const videoSearch = enrollmentViewContext?.videoSearch;

    const api = useCoreApi();
    const { t } = useTranslation("lms");
    const { showUser } = useModalUserProfile();
    const { checkLimit, limits } = useSystemUsages();

    const [publication, setPublication] = useState<TrainingPostItemDto>();
    const [cardOwnProps, setCardOwnProps] = useState<CardOwnPropsOptions>();
    const [loading, setLoading] = useState<boolean>(true);
    const [viewError, setViewError] = useState<any>();
    const [openSearch, setOpenSearch] = useState<boolean>(false);

    const [localContent, setLocalContent] = useState<string>("");
    const [loadingContent, setLoadingContent] = useState<boolean>(false);
    const [expanded, setExpanded] = useState<boolean>(false);
    const [openLightbox, setOpenLightbox] = useState<boolean>(false);
    const [chronometer, setChronometer] = useState<boolean>(false);
    const feedItemHelper = useRef<FeedItemHelper | undefined>(undefined);

    const currentMedia = useRef<FeedAttachDto | null>(null);
    const firstPlay = useRef<boolean>(true);

    const slides = useMemo(() => (
        publication && publication.presentation && publication.attachs
            ? BuildLigthBoxSlides(publication.presentation, publication.attachs, publication.presentation.rotationInterval == 0)
            : []
    ), [publication]);

    const [track, setTrack] = useState<TrackComponentStatus>(null!);

    /////////////////////////////////////////////////////////////////////////////////////

    ///
    const loadContent = (publicationId: string) => {
        setLoadingContent(true);
        api.v1.knowledgePublication.getContent({ publicationId: publicationId })
            .then((result) => {
                setLocalContent(result.data);
                setExpanded(true);
                setPublication(state => ({ ...state!, contentLoadStatus: FeedContentLoadStatus.AllContentLoaded }));
            })
            .catch((e) => console.error(e))
            .finally(() => setLoadingContent(false));
    };

    ///
    const toogleStatus = () => {
        if (publication!.contentLoadStatus === FeedContentLoadStatus.AllContentLoaded) {
            setExpanded(!expanded);
        }
        else if (publication!.contentLoadStatus === FeedContentLoadStatus.LoadContentInDemand) {
            loadContent(publication!.publicationId);
        }
    };

    ///
    const handleOpenAttach = (attachId: string) => {
        if (!readOnly && publication?.enrollmentData?.completeOnAttachOpened == true) {
            doTrackingContent(attachId, 0, true);
        }
        if (onAttachOpen) onAttachOpen();
    };

    ///
    const handleCloseAttach = (attachId: string) => {
        if (onAttachClose) onAttachClose();
    };

    /////////////////////////////////////////////////////////////////////////////////////
    /// TRACKING: Tempo de Visualização Mínimo
    /////////////////////////////////////////////////////////////////////////////////////
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [playbackTime, setPlaybackTime] = useState<number>(0);

    useDidUpdateEffect(() => {
        if (!readOnly) {
            if (publication!.enrollmentData) {

                const timeLeft = publication?.enrollmentData.completeOnMinTime == null
                    ? 0
                    : Math.max(0, publication?.enrollmentData.completeOnMinTime - (publication?.enrollmentData?.publicationTimeViewed ?? 0));

                if (publication!.enrollmentData.completeOnMinTime != null && timeLeft > 0) {
                    // Inicia ou continua o tracking da publicação.
                    setPlaybackTime(publication!.enrollmentData.publicationTimeViewed ?? 0);
                    setChronometer(true);
                }
                else if (publication!.enrollmentData.completeOnMinTime == null && publication!.enrollmentData.publicationTimeViewed == null) {
                    // Marca publicação como visualizada.
                    doTrackingPageView(0, true);
                }
                else {
                    setPlaybackTime(0);
                    setChronometer(false);
                }

                setTrack({
                    attachOpened: publication?.enrollmentData.completeOnAttachOpened == false,
                    mediaCompleted: publication?.enrollmentData.completeOnFullMediaPlayed === false,
                    publicationViewed: publication?.enrollmentData.completeOnMinTime == null,
                    timeLeft: timeLeft
                });
            }
        }
    }, [publication]);

    useDidUpdateEffect(() => {
        if (!readOnly && publication!.enrollmentData && publication!.enrollmentData.completeOnMinTime != null) {
            if (playbackTime > publication!.enrollmentData.completeOnMinTime) {
                // Completado.
                doTrackingPageView(playbackTime, true);
            }
            else if (playbackTime % TrackingSeconds == 0) {
                // Marcação Parcial (a cada 5s)
                doTrackingPageView(playbackTime, false);
            }
        }

    }, [playbackTime]);

    useDidUpdateEffect(() => {
        if (timer != null) clearInterval(timer.current!);
        if (chronometer) timer.current = setInterval(() => setPlaybackTime(state => state + 1), 1000);

        return () => {
            if (timer != null) clearInterval(timer.current!);
        };
    }, [chronometer]);

    /////////////////////////////////////////////////////////////////////////////////////
    /// IA
    /////////////////////////////////////////////////////////////////////////////////////

    const showIADialog = useMemo(() => {
        if (publication == null || limits == null)
            return false;

        const att = publication.attachs?.find(p => p.attachId == cardOwnProps?.presentation.currentAttachId);
        return att != null
            ? limits.callIaStatus == IaRequestCallingStatus.Ok && publication.allowIaExplains && (att.hasTranscription === true || att.hasSearchData === true)
            : limits.callIaStatus == IaRequestCallingStatus.Ok && publication.allowIaExplains;
    }, [publication, limits, cardOwnProps]);

    useEffect(() => {
        checkLimit();
    }, []);

    /////////////////////////////////////////////////////////////////////////////////////
    /// MEDIA
    /////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        currentMedia.current = publication?.attachs?.find(p => p.attachId == cardOwnProps?.presentation.currentAttachId) ?? null;
        firstPlay.current = true;

        //
        if (currentMedia.current && onPresentationMedia) {
            onPresentationMedia(currentMedia.current.attachType);
        }

    }, [cardOwnProps]);

    ///
    const onMediaPlay = (id: string, seconds: number) => {
        const m = currentMedia.current;
        if (m == null || m.mediaCompleted == true || !enrollmentId) {
            // Não precisa verificar SEEK.
            return null;
        }

        if (!firstPlay.current) {
            if (Math.floor(m.lastMediaPosition ?? 0) >= Math.floor(seconds))
                return null;

            // Não é a primeira vez que pressionou PLAY, então verificar se pode fazer SEEK.
            if (publication?.enrollmentData?.disableMediaSeek == true)
                return Math.min(seconds, m.lastMediaPosition ?? 0);

            return seconds;
        }

        // Ao pressionar PLAY a primeira vez, posiciona a mídia na última posição registrada.
        firstPlay.current = false;
        return m.lastMediaPosition ?? 0;
    };

    const onPause = (id: string, seconds: number) => {
        const m = currentMedia.current;
        if (m == null || m.mediaCompleted == true || publication?.enrollmentData?.disableMediaSeek == false) {
            // Não precisa verificar SEEK.
            return null;
        }
    };

    ///
    const onConfirmMediaSeek = (id: string, seekSeconds: number): CustomVideoConfirmSeekResult => {
        const m = currentMedia.current;
        return m == null || m.mediaCompleted == true || enrollmentId == null || publication!.enrollmentData?.disableMediaSeek == false
            ? { playTime: seekSeconds, threshold: TrackingLowerThresholdSeconds }
            : m.mediaCompleted == false
                ? { playTime: Math.min(seekSeconds, m.lastMediaPosition ?? 0), threshold: TrackingLowerThresholdSeconds }
                : { playTime: m.lastMediaPosition ?? 0, threshold: TrackingLowerThresholdSeconds };
    };

    ///
    const trackingProgress = (id: string, state: OnProgressProps) => {
        const m = currentMedia.current;
        if (m == null || m.mediaCompleted == true || m.streamingExceeded)
            return;

        if (state.playedSeconds > 0) {
            if (publication?.enrollmentData?.disableMediaSeek == true) {
                // Necessário devido ao SEEK.
                const lp = m.lastMediaPosition ?? 0;
                const diffTime = Math.trunc(state.playedSeconds - lp);
                if (diffTime > TrackingLowerThresholdSeconds) return;
            }

            trackingContent(id, state.playedSeconds, false);
        }
    };

    ///
    const trackingContent = (id: string, playedSeconds: number, endPlayback: boolean) => {
        const m = currentMedia.current;
        if (m && !m.streamingExceeded) {
            const lp = m.lastMediaPosition ?? 0;
            if (playedSeconds >= lp || endPlayback) {
                // Grava a cada n segundos ou se terminou.
                const diffTime = Math.trunc(playedSeconds - lp);
                // Tolerância caso não possa fazer SEEK.
                const maxDiffTime = publication!.enrollmentData?.disableMediaSeek == true ? TrackingLowerThresholdSeconds : Number.MAX_SAFE_INTEGER;

                if (endPlayback || diffTime <= maxDiffTime) {
                    // Salva última posição (localmente).
                    m.lastMediaPosition = endPlayback ? playedSeconds : Math.floor(playedSeconds);

                    // Salva o tracking assíncronamente.
                    doTrackingContent(id, playedSeconds, endPlayback);
                }
            }
        }
    };

    ///
    const checkCompleted = (trackReponse: TrackingContentResponse) => {
        if (trackReponse.trackResult && trackReponse.trackResult.completeChanged && trackReponse.trackResult.completed) {
            onComplete();
        }
    };

    ///
    const doTrackingContent = (id: string, playedSeconds: number, endOfMedia: boolean) => {
        if (readOnly === true || !enrollmentId || enrollmentId == NIL || !componentId) return;

        // Salva o tracking assíncronamente.
        api.v1.enrollment.trackingContent({
            enrollmentId: enrollmentId,
            componentId,
            attachId: id,
            playTime: playedSeconds,
            viewedOrFullPlayed: endOfMedia
        }).then((result) => {
            if (result.data?.success == true) {

                if (currentMedia.current) {
                    // Salva se componente foi completado (localmente).
                    currentMedia.current.mediaCompleted = result.data.trackResult != null && (result.data.trackResult.mediaCompleted || result.data.trackResult.completed);
                }

                if (result.data.trackResult) {
                    setTrack((state) => ({
                        ...state,
                        attachOpened: result.data.trackResult!.attachOpened,
                        mediaCompleted: result.data.trackResult!.mediaCompleted,
                    }));
                }

                checkCompleted(result.data);
            }
        });
    };

    ///
    const doTrackingPageView = (playedSeconds: number, fullViewed: boolean) => {
        if (readOnly === true || !enrollmentId || enrollmentId == NIL || !componentId) return;

        setChronometer(false);

        // Salva o tracking assíncronamente.
        api.v1.enrollment.trackingContent({
            enrollmentId: enrollmentId,
            componentId,
            attachId: NIL,
            playTime: playedSeconds,
            viewedOrFullPlayed: fullViewed
        }).then((result) => {
            if (result.data?.success == true) {

                if (result.data.trackResult) {
                    setTrack((state) => ({
                        ...state,
                        publicationViewed: result.data.trackResult!.publicationViewed,
                        timeLeft: Math.max(0, (track?.timeLeft ?? 0) - TrackingSeconds)
                    }));
                }

                checkCompleted(result.data);
            }
        }).finally(() => {
            if (!fullViewed) setChronometer(true);
        });
    };

    ///
    const RenderTrackInfo = useCallback(() => {
        const requisites: string[] = [];

        if (track?.mediaCompleted === false)
            requisites.push(t("Student.EnrollmentView.Content.Post.CompleteOnFullMediaPlayedText"));

        if (track?.attachOpened === false)
            requisites.push(t("Student.EnrollmentView.Content.Post.CompleteOnAttachOpenedText"));

        if (track?.publicationViewed === false) {
            if (track.timeLeft > 0) {
                const timeLeft = Formaters.timeSpanToTime(Formaters.playTimer(track.timeLeft));
                requisites.push(t("Student.EnrollmentView.Content.Post.CompleteOnMinTimeText") + " " + timeLeft);
            }
        }

        return requisites.length == 0
            ? null
            : (
                <Grid id="classificationSection" width="100%" >
                    <Typography variant="bodyNotice">
                        {t("Student.EnrollmentView.ToCompleteText")}
                        <MprcJoinComponents components={requisites} separator="&nbsp;&bull;&nbsp;" />
                    </Typography>
                </Grid >
            );
    }, [publication, t, track]);

    /////////////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    /////////////////////////////////////////////////////////////////////////////////////

    useDidUpdateEffect(() => {
        setLoading(true);
    }, [enrollmentId, componentId]);

    useEffect(() => {
        async function getData() {
            try {

                var response;
                if (enrollmentId && componentId) {
                    response = await api.v1.enrollment.getPostItem({ enrollmentId, componentId });
                } else if (publicationId) {
                    response = await api.v1.lmsPublication.getTrainingPostItem({ componentId: null, publicationId: publicationId! });
                } else {
                    response = await api.v1.lmsPublication.getTrainingPostItem({ componentId: componentId!, publicationId: null });
                }

                feedItemHelper.current = new FeedItemHelper(response.data as unknown as FeedItemDto);

                const ownProps = PrepareCardOwnProps(response.data as unknown as FeedItemDto, feedItemHelper.current, videoSearch);
                ownProps.videoAutoPlay = true;

                setCardOwnProps(ownProps);
                setPublication(response.data as TrainingPostItemDto);
            } catch (error) {
                setViewError(error);
            }
            finally {
                setLoading(false);
            }
        }

        if (loading) getData();
    }, [loading]);

    useEffect(() => {
        setCardOwnProps((state) => {
            const newState = { ...state!, search: videoSearch } as CardOwnPropsOptions;
            return newState;
        });
    }, [videoSearch]);

    /////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    /////////////////////////////////////////////////////////////////////////////////////
    const trackInfo = RenderTrackInfo();

    return (
        <>
            {loading
                ? <Skeleton variant="rectangular" width="100%" height={"500px"} />
                :
                viewError
                    ? <MprcRequestErrorReport error={viewError} />
                    : publication &&
                    (
                        <MprcContainer margin={enrollmentId ? "normal" : "none"}>
                            <Card elevation={!enrollmentId ? 0 : undefined}>
                                <CardContent sx={{ p: 1, pb: 0 }}>

                                    {openSearch &&
                                        <PublicationAiSearchDialog
                                            publicationId={publication.publicationId}
                                            attachId={cardOwnProps!.presentation?.currentAttachId}
                                            title={publication.title}
                                            onClose={() => setOpenSearch(false)}
                                        />
                                    }

                                    <Grid container width="100%" direction="column" spacing={1}>
                                        {!readOnly && trackInfo != null &&
                                            <Grid width="100%">
                                                {trackInfo}
                                            </Grid>
                                        }
                                        <Grid id="mediaHeaderSection" width="100%">
                                            <InternalContentMedia
                                                publicationId={publication.publicationId}
                                                presentation={publication.presentation}
                                                attachs={publication.attachs}
                                                cardOwnProps={cardOwnProps!}
                                                onUpdateCardOwnProps={(data: Partial<CardOwnPropsOptions>) =>
                                                    setCardOwnProps((state) => {
                                                        const newState = { ...state!, ...data };
                                                        return newState;
                                                    })
                                                }
                                                videoEvents={
                                                    readOnly
                                                        ? {}
                                                        : {
                                                            onPlay: onMediaPlay,
                                                            onPause: onPause,
                                                            onProgress: trackingProgress,
                                                            onEnded: (id, playedSeconds) => trackingContent(id, playedSeconds, true),
                                                            onConfirmSeek: onConfirmMediaSeek
                                                        }
                                                }
                                            />
                                            {
                                                feedItemHelper.current != undefined &&
                                                (feedItemHelper.current.hasAutoPlayPresentation || feedItemHelper.current.hasPresentation) &&
                                                <Grid container spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 1 }}>
                                                    {
                                                        feedItemHelper.current.hasAutoPlayPresentation &&
                                                        <Grid>
                                                            <MprcSwitchPlayback
                                                                checked={cardOwnProps!.presentation.autoplay}
                                                                onChange={
                                                                    (_, checked) => setCardOwnProps({ ...cardOwnProps!, presentation: { ...cardOwnProps!.presentation, autoplay: checked } })
                                                                }
                                                            />
                                                        </Grid>
                                                    }
                                                    {
                                                        feedItemHelper.current.hasPresentation && publication.presentation &&
                                                        <Grid>
                                                            <Typography variant="iconText">
                                                                <IconButton color="inherit"
                                                                    onClick={() => {
                                                                        setOpenLightbox(true);
                                                                        setCardOwnProps(state => ({ ...state!, presentation: { ...state!.presentation, lighboxOpened: true } }));
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faPresentationScreen} color="inherit" />
                                                                </IconButton>
                                                            </Typography>
                                                            <CardLightBox
                                                                slides={slides}
                                                                open={openLightbox}
                                                                index={0}
                                                                autoplay={publication.presentation.rotationInterval > 0}
                                                                rotationInterval={publication.presentation.rotationInterval > 0 ? publication.presentation!.rotationInterval : 5}
                                                                onClose={() => {
                                                                    setOpenLightbox(false);
                                                                    setCardOwnProps(state => ({ ...state!, presentation: { ...state!.presentation, lighboxOpened: false } }));
                                                                }}
                                                            />
                                                        </Grid>
                                                    }
                                                </Grid>
                                            }
                                        </Grid>
                                        <Grid width="100%">
                                            <Grid container direction="column">
                                                <Grid id="titleSection">

                                                    <Grid container justifyContent="space-between" alignItems="center">
                                                        <MprcFeedTypography variant="title" component="div" noWrap={true} color="black">
                                                            <span title={publication.title}>{publication.title}</span>
                                                        </MprcFeedTypography>

                                                        {
                                                            publication.copyFromKnowledge &&
                                                            <Grid>
                                                                <IconButton onClick={() => showUser(publication.author.userId)} title={t("knowledge:Feed.Card.Common.AuthorInfoTooltip")}>
                                                                    <FontAwesomeIcon icon={faCreativeCommonsBy} />
                                                                </IconButton>
                                                            </Grid>
                                                        }
                                                    </Grid>

                                                </Grid>
                                                <Grid id="contentSection">
                                                    <Grid container gap={1} justifyContent="center">
                                                        <Grid id="contentSection" className={AppGlobalClassKeys.qlContentSection} width="100%">
                                                            <MprcFeedTypography variant="content" component="div">
                                                                <div dangerouslySetInnerHTML={{
                                                                    __html: !expanded || publication.contentLoadStatus == FeedContentLoadStatus.NoActionNecessary
                                                                        ? publication.content ?? ""
                                                                        : localContent
                                                                }}></div>
                                                            </MprcFeedTypography>
                                                            {
                                                                publication.contentLoadStatus !== FeedContentLoadStatus.NoActionNecessary &&
                                                                <>
                                                                    {
                                                                        loadingContent
                                                                            ? <MprcSpinnerIndicator />
                                                                            : <MprcFeedTypography variant="moreContent" component="a" onClick={() => toogleStatus()}>
                                                                                {
                                                                                    expanded
                                                                                        ? t("knowledge:Feed.Card.Common.SeeLessText")
                                                                                        : t("knowledge:Feed.Card.Common.SeeMoreText")
                                                                                }
                                                                            </MprcFeedTypography>
                                                                    }
                                                                </>
                                                            }
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid id="attachSection" width="100%">
                                            <InternalAttach
                                                publicationId={publication.publicationId}
                                                attachs={publication.attachs}
                                                helper={feedItemHelper.current!}
                                                onOpen={handleOpenAttach}
                                                onClose={handleCloseAttach}
                                            />
                                        </Grid>
                                        <Grid id="footerSection">
                                            {showIADialog &&
                                                <MprcClickable onClick={() => setOpenSearch(true)}>
                                                    <IconAI fixedWidth /> {t("knowledge:Feed.AITranscriptionSearch")}
                                                </MprcClickable>
                                            }
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </MprcContainer>
                    )
            }
        </>
    );
};
